import { get, partition } from 'lodash/fp';
import {
  IAddressFieldInfo,
  IGroupedFields,
  ISectionField,
  metaAddressGroupRoles,
  MetaAddressGroupRoles,
  metaGroupTypes,
  MetaGroupTypes,
} from '@whitelabel/helpers/types';
import { updateClaimantKeys } from './utils';

/**
 * Get a list of names, labels, descriptions, and etc from the address group
 */
export const getAddressFieldInfo = (addressFields: ISectionField[]) => {
  const allAddressFieldData = updateClaimantKeys<IAddressFieldInfo<ISectionField>>(
    addressFields.reduce((acc, field) => {
      const role = field.meta?.group?.role as MetaAddressGroupRoles;
      if (!!role && metaAddressGroupRoles.includes(role)) {
        return { ...acc, [role]: field };
      }
      return acc;
    }, {} as Record<MetaAddressGroupRoles, ISectionField>),
    true,
  );

  function mapAddressValue<T>(property: string) {
    return Object.keys(allAddressFieldData).reduce(
      (acc, key) => ({
        ...acc,
        [key]: get(property, allAddressFieldData[key as keyof IAddressFieldInfo<ISectionField>]) as T,
      }),
      {} as T,
    );
  }

  const nameList = mapAddressValue<IAddressFieldInfo<string>>('name');
  const labelList = mapAddressValue<IAddressFieldInfo<string | undefined>>('meta.label');
  const descriptionList = mapAddressValue<IAddressFieldInfo<string | undefined>>('meta.description');
  const placeholderList = mapAddressValue<IAddressFieldInfo<string | undefined>>('meta.placeholder');
  const requiredList = mapAddressValue<IAddressFieldInfo<boolean>>('required');

  return {
    address1Field: allAddressFieldData.address1,
    nameList,
    labelList,
    descriptionList,
    placeholderList,
    requiredList,
  };
};

/**
 * transform the section fields into a list of grouped fields by group type and name
 * if the field doesn't have a group, it will be grouped into a 'Non_Group' group
 * @param sectionFields ISectionField[]
 * @returns IGroupedFields[]
 */
export const groupFieldsByGroupTypeAndName = (sectionFields: ISectionField[]): IGroupedFields[] =>
  sectionFields.reduce((acc, field) => {
    const type = field?.meta?.group?.type ?? 'non_group_fields';
    const name = field?.meta?.group?.name ?? 'non_group_fields';

    // find the existing group fields by group type and name
    const [prevGroupFields, restGroupFields] = partition<IGroupedFields>(
      (groupedField) => groupedField.name === name && groupedField.type === type,
      acc,
    );

    return [
      ...restGroupFields,
      ...(prevGroupFields?.[0]
        ? // if the group already exists, add the field to the group
          [{ ...prevGroupFields?.[0], fields: [...prevGroupFields?.[0].fields, field] }]
        : // if the group doesn't exist, create a new group with the field
          [{ fields: [field], type, name } as IGroupedFields]),
    ];
  }, [] as IGroupedFields[]);

/**
 * process the grouped fields and return a list of section fields
 * for non-group fields, return the fields as is
 * for group fields, process the fields and combine into a single section field
 * @param groupedFields IGroupedFields[]
 * @returns ISectionField[]
 */
export const processGroupFields = (groupedFields: IGroupedFields[]): ISectionField[] => {
  const [groupFields, nonGroupFields] = partition<IGroupedFields>(
    ({ type }) => metaGroupTypes.includes(type as MetaGroupTypes),
    groupedFields,
  );

  const flatNonGroupFields = nonGroupFields.reduce((acc, { fields }) => [...acc, ...fields], [] as ISectionField[]);
  const combineGroupFields = groupFields.reduce((acc, { fields, type }) => {
    switch (type) {
      case 'address': {
        const { address1Field, nameList, labelList, descriptionList, placeholderList, requiredList } =
          getAddressFieldInfo(fields);

        const groupedAddress: ISectionField = {
          conditions: null,
          id: address1Field.id,
          meta: { label: address1Field.meta?.label as string },
          name: address1Field.meta?.group?.name as string,
          options: null,
          ordering: address1Field.ordering,
          readOnly: false,
          required: false,
          type: 'FNOLAddress',
          validation: null,
          nameList,
          labelList,
          descriptionList,
          placeholderList,
          requiredList,
        };
        return [...acc, groupedAddress];
      }

      case 'currency_amount': {
        const amountField = fields.find((field) => field.meta?.group?.role === 'amount')!;
        const currencyField = fields.find((field) => field.meta?.group?.role === 'currency')!;

        const groupedCurrencyAmount: ISectionField = {
          conditions: null,
          id: amountField.id,
          meta: {
            label: amountField.meta?.label as string,
            description: amountField.meta?.description,
            placeholder: amountField.meta?.placeholder,
          },
          name: amountField.name,
          options: null,
          ordering: amountField.ordering,
          readOnly: amountField.readOnly,
          required: amountField.required,
          type: 'currencyAmount',
          validation: amountField.validation,
          bundledCurrencyFieldName: currencyField.name,
        };
        return [...acc, groupedCurrencyAmount];
      }
      default:
        return acc;
    }
  }, [] as ISectionField[]);

  return [...flatNonGroupFields, ...combineGroupFields];
};

export const processMetaDisplayFields = (fields: ISectionField[]): ISectionField[] =>
  fields.map((field) => {
    // transform meta display dob fields to dob type field
    if (field?.meta?.display === 'dob' && field.type === 'date') {
      return {
        ...field,
        type: 'dob',
      };
    }
    return field;
  });
